import type { AudioSettings } from '~/stores/auth'
import type { AudioPlaylistItem } from '~/composables/useCharacterPage'

export interface AudioPlayerState {
  isPlaying: Ref<boolean>
  autoplayBlocked: Ref<boolean>
  currentTrack: ComputedRef<AudioPlaylistItem | null>
  volume: Ref<number>
  volumeIcon: ComputedRef<string>
  currentTime: Ref<number>
  duration: Ref<number>
  progress: ComputedRef<number>
  hasMultipleTracks: ComputedRef<boolean>
  togglePlay: () => void
  play: () => void
  pause: () => void
  next: () => void
  prev: () => void
  seek: (ratio: number) => void
  setVolume: (value: number) => void
  toggleMute: () => void
  pageEntered: Ref<boolean>
  enterPage: () => void
  enterWithoutAudio: () => void
  formatTime: (seconds: number) => string
}

export function useAudioPlayer(
  playlist: Ref<AudioPlaylistItem[]> | ComputedRef<AudioPlaylistItem[]>,
  audioSettings: Ref<AudioSettings> | ComputedRef<AudioSettings>
): AudioPlayerState {
  const audio = ref<HTMLAudioElement | null>(null)
  const currentIndex = ref(0)
  const isPlaying = ref(false)
  const autoplayBlocked = ref(false)
  const pageEntered = ref(false)
  const initialVolume = Math.min(audioSettings.value.volume ?? 0.5, 0.5)
  const volume = ref(initialVolume)
  const previousVolume = ref(initialVolume)
  const currentTime = ref(0)
  const duration = ref(0)

  const currentTrack = computed(() => {
    if (playlist.value.length === 0) return null
    return playlist.value[currentIndex.value] ?? playlist.value[0]
  })

  const progress = computed(() => {
    if (!duration.value || !isFinite(duration.value)) return 0
    return (currentTime.value / duration.value) * 100
  })

  const volumeIcon = computed(() => {
    if (volume.value === 0) return 'i-lucide-volume-x'
    if (volume.value < 0.5) return 'i-lucide-volume-1'
    return 'i-lucide-volume-2'
  })

  const hasMultipleTracks = computed(() => playlist.value.length > 1)

  function formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  function pickNextIndex(): number {
    if (playlist.value.length <= 1) return 0
    if (audioSettings.value.shuffle) {
      let next: number
      do {
        next = Math.floor(Math.random() * playlist.value.length)
      } while (next === currentIndex.value)
      return next
    }
    return (currentIndex.value + 1) % playlist.value.length
  }

  function play() {
    if (!audio.value) return
    audio.value.volume = volume.value
    const promise = audio.value.play()
    if (promise) {
      promise.then(() => {
        isPlaying.value = true
        autoplayBlocked.value = false
        pageEntered.value = true
      }).catch(() => {
        autoplayBlocked.value = true
      })
    }
  }

  function enterPage() {
    autoplayBlocked.value = false
    pageEntered.value = true
    play()
  }

  function enterWithoutAudio() {
    autoplayBlocked.value = false
    pageEntered.value = true
  }

  function pause() {
    audio.value?.pause()
    isPlaying.value = false
  }

  function togglePlay() {
    if (isPlaying.value) pause()
    else play()
  }

  function next() {
    currentIndex.value = pickNextIndex()
    nextTick(() => play())
  }

  function prev() {
    if (playlist.value.length <= 1) return
    currentIndex.value = currentIndex.value === 0
      ? playlist.value.length - 1
      : currentIndex.value - 1
    nextTick(() => play())
  }

  function seek(ratio: number) {
    if (audio.value && isFinite(duration.value) && duration.value > 0) {
      audio.value.currentTime = ratio * duration.value
    }
  }

  function setVolume(value: number) {
    volume.value = value
    if (audio.value) {
      audio.value.volume = value
      audio.value.muted = value === 0
    }
  }

  function toggleMute() {
    if (volume.value > 0) {
      previousVolume.value = volume.value
      volume.value = 0
    } else {
      volume.value = previousVolume.value || 0.5
    }
    if (audio.value) {
      audio.value.volume = volume.value
      audio.value.muted = volume.value === 0
    }
  }

  // Keyboard controls (Space = play/pause, Arrow Up/Down = volume)
  function onKeyDown(e: KeyboardEvent) {
    const el = e.target as HTMLElement
    if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable) return
    if (autoplayBlocked.value) return

    if (e.code === 'Space') {
      e.preventDefault()
      togglePlay()
    } else if (e.code === 'ArrowUp') {
      e.preventDefault()
      setVolume(Math.min(1, volume.value + 0.1))
    } else if (e.code === 'ArrowDown') {
      e.preventDefault()
      setVolume(Math.max(0, volume.value - 0.1))
    }
  }

  // Create Audio element on mount
  onMounted(async () => {
    window.addEventListener('keydown', onKeyDown)
    audio.value = new Audio()
    audio.value.preload = 'auto'

    audio.value.addEventListener('timeupdate', () => {
      if (audio.value) currentTime.value = audio.value.currentTime
    })
    audio.value.addEventListener('loadedmetadata', () => {
      if (audio.value) duration.value = audio.value.duration
    })
    audio.value.addEventListener('ended', () => {
      next()
    })

    // Set initial track
    if (playlist.value.length > 0) {
      if (audioSettings.value.shuffle) {
        currentIndex.value = Math.floor(Math.random() * playlist.value.length)
      }
      if (currentTrack.value?.url) {
        audio.value.src = currentTrack.value.url
      }
      await nextTick()
      play()
    }
  })

  // Watch for track changes
  watch(currentTrack, (track) => {
    if (audio.value && track?.url) {
      audio.value.src = track.url
      audio.value.load()
    }
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeyDown)
    if (audio.value) {
      audio.value.pause()
      audio.value.removeAttribute('src')
      audio.value = null
    }
  })

  return {
    isPlaying,
    autoplayBlocked,
    pageEntered,
    currentTrack,
    volume,
    volumeIcon,
    currentTime,
    duration,
    progress,
    hasMultipleTracks,
    togglePlay,
    play,
    pause,
    next,
    prev,
    seek,
    setVolume,
    toggleMute,
    enterPage,
    enterWithoutAudio,
    formatTime
  }
}
